import { platform } from 'constants'
import { useTheme } from 'contexts'
import { Field as FormikField, FieldProps as FormikFieldProps } from 'formik'
import React, { useState } from 'react'
import { KeyboardTypeOptions, StyleSheet } from 'react-native'

import { Box } from 'widgets/Box'
import { Dropdown } from 'widgets/Dropdown'
import { Text } from 'widgets/Text'
import { TextInput } from 'widgets/TextInput'

type InputControlType = 'text' | 'password' | 'select' | 'textarea'

interface BorderlessFieldProps {
  placeholder?: string
  name?: string
  type?: InputControlType
  editable?: boolean
  label?: string
  required?: boolean
  hidden?: boolean
  // eslint-disable-next-line react/no-unused-prop-types
  disabled?: boolean
  // eslint-disable-next-line react/no-unused-prop-types
  isSearchable?: boolean
  // eslint-disable-next-line react/no-unused-prop-types
  keyboardType?: KeyboardTypeOptions
  // eslint-disable-next-line react/no-unused-prop-types
  icon?: string
  // eslint-disable-next-line react/no-unused-prop-types
  options?: any[]

  value?: any
  // eslint-disable-next-line react/no-unused-prop-types
  format?: (value: string) => string
  // eslint-disable-next-line react/no-unused-prop-types
  multiSelect?: boolean
  // eslint-disable-next-line react/no-unused-prop-types
  theme: any
}

export const BorderlessField = (props: BorderlessFieldProps) => {
  const [open, setOpen] = useState(false)
  const theme = useTheme()

  const {
    name,
    type = 'text',
    placeholder,
    editable = false,
    label,
    hidden = false,
    required = false,
    value,
  } = props

  const finalProps = {
    ...props,
    onOpen: () => setOpen(true),
    onClose: () => setOpen(false),
    theme,
  }

  if (hidden) return null

  return (
    <Box padding={{ vertical: platform.IS_WEB ? 'sm' : 'xxs' }}>
      {label ? (
        <Box padding={{ vertical: 'sm' }}>
          <Text bold fontSize='sm'>
            {label} {required && <Text color='error'>*</Text>}
          </Text>
        </Box>
      ) : null}
      <FormikField editable={editable} type={type} name={name} placeholder={placeholder}>
        {InputControl(finalProps)}
      </FormikField>
    </Box>
  )
}

const InputControl =
  (props: BorderlessFieldProps) =>
  ({ field, meta, form }: FormikFieldProps) => {
    const { isSearchable = true, type = 'text', placeholder, multiSelect = false, theme } = props
    const isSecureTextEntry = type === 'password'
    const isMultiLine = type === 'textarea'

    const styles = StyleSheet.create({
      textInput: {
        color: theme.colors.black,
        padding: isMultiLine ? 0 : 10,
        height: isMultiLine ? 200 : undefined,
      },
    })

    switch (type) {
      case 'text':
      case 'password':
      case 'textarea':
        return (
          <TextInput
            {...props}
            {...field}
            secureTextEntry={isSecureTextEntry}
            error={meta?.error}
            touched={meta?.touched}
            numberOfLines={4}
            multiline={isMultiLine}
            backgroundColor='white'
            placeholderTextColor='greyDark'
            style={styles.textInput}
          />
        )
      case 'select':
        return (
          <Dropdown
            multiSelect={multiSelect}
            onMultiSelectChange={(value) => form.setFieldValue(field.name, value)}
            {...props}
            {...field}
            placeholderText={placeholder}
            isSearchable={isSearchable}
            secureTextEntry={isSecureTextEntry}
            error={meta?.error}
            touched={meta?.touched}
          />
        )
      default:
        return <Box />
    }
  }
